import { Fragment, useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { ChevronDown, ChevronRight, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { DuplicateGroup } from '@/hooks/useStripeCleanup';
import { DuplicateDetailRow } from './DuplicateDetailRow';

interface DuplicatesTableProps {
  duplicates: DuplicateGroup[];
  selectedEmails: Set<string>;
  executionResults: { success: boolean; email: string; error?: string }[];
  onToggleSelection: (email: string) => void;
}

export function DuplicatesTable({
  duplicates,
  selectedEmails,
  executionResults,
  onToggleSelection,
}: DuplicatesTableProps) {
  const [expandedEmails, setExpandedEmails] = useState<Set<string>>(new Set());

  const toggleExpand = (email: string) => {
    setExpandedEmails(prev => {
      const newSet = new Set(prev);
      if (newSet.has(email)) {
        newSet.delete(email);
      } else {
        newSet.add(email);
      }
      return newSet;
    });
  };

  const getExecutionStatus = (email: string) => {
    const result = executionResults.find(r => r.email === email);
    if (!result) return 'pending';
    return result.success ? 'success' : 'error';
  };

  const getActionsLabel = (group: DuplicateGroup) => {
    if (group.merge_actions.length === 0) return 'Nenhuma ação';
    
    // merge_actions já é um array de strings descritivas
    const actions = group.merge_actions;
    if (actions.length <= 2) {
      return actions.join(', ');
    }
    return `${actions.slice(0, 2).join(', ')} +${actions.length - 2}`;
  };

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12"></TableHead>
            <TableHead className="w-12"></TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Manter</TableHead>
            <TableHead className="text-center">Duplicados</TableHead>
            <TableHead>Ações Planejadas</TableHead>
            <TableHead className="text-center">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {duplicates.map((group) => {
            const isExpanded = expandedEmails.has(group.email);
            const isSelected = selectedEmails.has(group.email);
            const status = getExecutionStatus(group.email);

            return (
              <Fragment key={group.email}>
                <TableRow 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleExpand(group.email)}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleSelection(group.email)}
                      disabled={status === 'success'}
                    />
                  </TableCell>
                  <TableCell>
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm">{group.email}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">
                        {group.keep.id.slice(0, 12)}...
                      </span>
                      {group.keep.name && (
                        <span className="text-sm">({group.keep.name})</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary">{group.merge_from.length}</Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {getActionsLabel(group)}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    {status === 'success' && (
                      <Badge variant="default" className="bg-green-600">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Concluído
                      </Badge>
                    )}
                    {status === 'error' && (
                      <Badge variant="destructive">
                        <XCircle className="h-3 w-3 mr-1" />
                        Erro
                      </Badge>
                    )}
                    {status === 'pending' && (
                      <Badge variant="outline">
                        <Clock className="h-3 w-3 mr-1" />
                        Pendente
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>

                {isExpanded && (
                  <TableRow>
                    <TableCell colSpan={7} className="bg-muted/30 p-0">
                      <DuplicateDetailRow group={group} />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
